import { QuestionOutlined, ThunderboltOutlined } from "@ant-design/icons";
import { Alert, Button, message, Modal, Space, Tag } from "antd";
import dayjs from "dayjs";
import React, { useState } from "react";
import { HelpLine } from "~/components/help/helpLine.jsx";
import { $t, i18n } from "~/i18n.js";
import { api } from "~/lib/api.js";

const ClubDirectDebitExplainer = ({ club }) => (
  <HelpLine style={{ marginBottom: 16 }}>
    {club.direct_debit_mode === "monthly" &&
      $t(
        "Met automatische incasso wordt je saldo op de 1e van elke maand automatisch geïncasseerd, indien saldo lager is dan ${balance}.",
        {
          balance: i18n.parseLocaleCurrencyString(
            club.direct_debit_min_balance || -100,
          ),
        },
      )}
    {club.direct_debit_mode === "balance" &&
      $t(
        "Met automatische incasso wordt je saldo automatisch geïncasseerd zodra je saldo lager is dan ${balance}.",
        {
          balance: i18n.parseLocaleCurrencyString(
            club.direct_debit_min_balance || -100,
          ),
        },
      )}

      {club.direct_debit_mode === "manual" && (
        $t(
          "Met automatische incasso kan de club je negatieve saldo afschrijven van je bankrekening.",
        )
      )}
  </HelpLine>
);

export const DirectDebit = ({
  club,
  setMandateInfo,
  paymentTestMode,
  mandateInfo,
}) => {
  const [mandateModalOpen, setMandateModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [mandateLoading, setMandateLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);

  const mandateStatus = mandateInfo?.status || "";
  const mandateDateCreated = mandateInfo?.date_created;

  const initiateMandatePayment = async () => {
    setMandateLoading(true);
    const data = await api.get("financial/accounts/initiate_mandate.json");
    if (data.payment_link) {
      window.location.href = data.payment_link;
    } else {
      message.error($t("Fout bij het aanmaken van de betaling"));
      setMandateLoading(false);
    }
  };

  const handleWithdrawMandate = async () => {
    setWithdrawLoading(true);
    const data = await api.get("financial/accounts/withdraw_mandate.json");
    if (data) {
      setMandateInfo({
        status: "",
        date_created: null,
        last_three_digits_iban: null,
      });
    }
    setWithdrawLoading(false);
    setWithdrawModalOpen(false);
  };

  if (!club.enable_direct_debit || club.direct_debit_status !== "active") {
    return null;
  }

  // Status: Empty or invalid - show activate button
  const showActivateButton =
    !mandateStatus || mandateStatus === "" || mandateStatus === "invalid" || mandateStatus === "404";
  console.log('DirectDebit render - mandateStatus:', mandateStatus, 'showActivateButton:', showActivateButton);
  
  // Status: Pending - show info message about payment being processed
  const showPendingStatus = mandateStatus === "pending";

  // Status: Expired or Withdrawn - show tag and reactivate button
  const showReactivateButton =
    mandateStatus === "expired" || mandateStatus === "withdrawn";

  // Status: Valid - show active status with info and withdraw button
  const showActiveStatus = mandateStatus === "valid";

  return (
    <>
      {showActivateButton && (
        <div className="row" style={{ marginTop: 16, gap: 6 }}>
          <Button
            onClick={() => setMandateModalOpen(true)}
            type={paymentTestMode ? "default" : "primary"}
            icon={<ThunderboltOutlined />}
            disabled={paymentTestMode}
          >
            {$t("Activeer automatische incasso")}
          </Button>

          <Button
            onClick={() => setMandateModalOpen(true)}
            icon={<QuestionOutlined />}
            shape="circle"
            disabled={paymentTestMode}
          />
        </div>
      )}

      {showPendingStatus && (
        <div style={{ marginTop: 16 }}>
          <p>
            <span className="label">{$t("Automatische incasso")}</span>
            <Tag color="orange">{$t("In behandeling")}</Tag>
          </p>
          <HelpLine style={{ marginTop: 8 }}>
            {$t(
              "Je aanvraag voor de automatische incasso is in behandeling, verwerking kan enkele uren duren. Druk op 'Check betalingen' op je accountpagina om de status te verversen.",
            )}
          </HelpLine>
        </div>
      )}

      {showReactivateButton && (
        <div style={{ marginTop: 16 }}>
          <Space>
            <p>
              <span className="label">{$t("Automatische incasso")}</span>
              <Tag color="orange">
                {mandateStatus === "expired"
                  ? $t("Verlopen")
                  : $t("Ingetrokken")}
              </Tag>
            </p>
            <Button
              onClick={() => setMandateModalOpen(true)}
              type="primary"
              size="small"
              disabled={paymentTestMode}
            >
              {$t("Heractiveren")}
            </Button>
          </Space>
        </div>
      )}

      {showActiveStatus && (
        <div className="row" style={{ marginTop: 16, gap: 6 }}>
          <p>
            <span className="label">{$t("Automatische incasso")}</span>
            <Tag color="green">{$t("Actief")}</Tag>
          </p>
          <Button
            icon={<QuestionOutlined />}
            shape="circle"
            size="small"
            style={{ marginLeft: 8 }}
            onClick={() => {
              Modal.info({
                title: $t("Automatische incasso"),
                content: (
                  <div>
                    <p>
                      <strong>{$t("Geactiveerd op")}:</strong>{" "}
                      {mandateDateCreated
                        ? dayjs(mandateDateCreated).format("DD-MM-YYYY")
                        : "-"}
                    </p>
                    {mandateInfo?.last_three_digits_iban && (
                      <p>
                        <strong>{$t("Voor bankrekening eindigt op")}:</strong>{" "}
                        ***{mandateInfo.last_three_digits_iban}
                      </p>
                    )}

                    <div style={{ marginTop: 16 }}>
                      <ClubDirectDebitExplainer club={club} />
                    </div>
                  </div>
                ),
                okText: $t("Sluiten"),
                footer: (_, { OkBtn }) => (
                  <>
                    <Button
                      onClick={() => {
                        Modal.destroyAll();
                        setWithdrawModalOpen(true);
                      }}
                      type="default"
                      danger
                    >
                      {$t("Intrekken")}
                    </Button>
                    <OkBtn />
                  </>
                ),
              });
            }}
          />
        </div>
      )}

      <Modal
        open={mandateModalOpen}
        onCancel={() => {
          setMandateModalOpen(false);
          setMandateLoading(false);
        }}
        title={$t("Activeer automatische incasso")}
        footer={null}
      >
        <ClubDirectDebitExplainer club={club} />

        {i18n.currency === 'GBP' ? (
          <p>
            {$t(
              "Bij bevestiging word je doorgestuurd naar een beveiligde pagina van Mollie (voorheen GoCardless) waar je je bankgegevens veilig kunt achterlaten. Deze gegevens worden versleuteld opgeslagen en zijn niet zichtbaar voor de club, maar maken wel automatische incasso mogelijk.",
            )}
          </p>
        ) : (
          <>
            <p>
              {$t(
                "Om automatische incasso te activeren, moet je een eenmalige betaling doen. Je wordt doorgestuurd naar beveiligde betalingsomgeving waar je je bankgegevens kunt invoeren.",
              )}
            </p>
            <p style={{ marginTop: 6 }}>
              {$t(
                "Na deze betaling wordt je bankrekening gebruikt voor toekomstige automatische betalingen.",
              )}
            </p>
          </>
        )}

        {paymentTestMode && (
          <Alert
            message={$t(
              "Betalingen zijn nog niet geactiveerd. Zodra de betalingen geactiveerd zijn kan je hier de automatische incasso activeren.",
            )}
            showIcon
            style={{ marginTop: 8 }}
          />
        )}

        <Button
          type="primary"
          onClick={initiateMandatePayment}
          loading={mandateLoading}
          disabled={paymentTestMode}
          style={{ width: "100%", marginTop: 16 }}
        >
          {i18n.currency === 'GBP'
            ? $t("Activeer Direct Debit")
            : $t("Betaal ${amount} en activeer", {
                amount: i18n.parseLocaleCurrencyString(1),
              })}
        </Button>

        <div style={{ textAlign: 'left', margin: '16px 0' }}>
          <img 
            src="/mollie.png" 
            alt="Powered by Mollie" 
            style={{ maxWidth: '250px', height: 'auto' }}
          />
        </div>
      </Modal>

      <Modal
        open={withdrawModalOpen}
        onCancel={() => setWithdrawModalOpen(false)}
        title={$t("Automatische incasso intrekken")}
        footer={null}
      >
        <p>
          {$t(
            "Weet je zeker dat je de automatische incasso wilt intrekken? Je kunt deze later opnieuw activeren.",
          )}
        </p>

        <Alert
          message={$t("Let op")}
          description={$t(
            "Na het intrekken worden er geen automatische betalingen meer van je rekening afgeschreven.",
          )}
          type="warning"
          showIcon
          style={{ marginTop: 16, marginBottom: 16 }}
        />

        <Space style={{ width: "100%", justifyContent: "flex-end" }}>
          <Button onClick={() => setWithdrawModalOpen(false)}>
            {$t("Annuleren")}
          </Button>
          <Button
            type="primary"
            danger
            loading={withdrawLoading}
            onClick={handleWithdrawMandate}
          >
            {$t("Intrekken")}
          </Button>
        </Space>
      </Modal>
    </>
  );
};
